"use client";

import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

function ProcessSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="skeleton-card min-h-[200px]">
          <div className="flex items-center gap-4 mb-5">
            <div className="skeleton skeleton-icon" style={{ marginBottom: 0 }} />
            <div className="skeleton skeleton-line" style={{ width: '50px', height: '30px', marginBottom: 0 }} />
          </div>
          <div className="skeleton skeleton-line skeleton-line-medium mb-3" style={{ height: '18px' }} />
          <div className="skeleton skeleton-line skeleton-line-full" />
          <div className="skeleton skeleton-line skeleton-line-full" />
          <div className="skeleton skeleton-line skeleton-line-medium" />
        </div>
      ))}
    </div>
  );
}

export default function ProcessSection() {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.1 });
  const [activeStep, setActiveStep] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);

  const steps = [
    {
      number: '01',
      title: t('step1_title'),
      desc: t('step1_desc'),
      icon: (
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
      ),
    },
    {
      number: '02',
      title: t('step2_title'),
      desc: t('step2_desc'),
      icon: (
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
        </svg>
      ),
    },
    {
      number: '03',
      title: t('step3_title'),
      desc: t('step3_desc'),
      icon: (
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        </svg>
      ),
    },
    {
      number: '04',
      title: t('step4_title'),
      desc: t('step4_desc'),
      icon: (
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
        </svg>
      ),
    },
  ];

  useEffect(() => {
    const loadTimer = setTimeout(() => setLoaded(true), 500);
    return () => clearTimeout(loadTimer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <section id="process" ref={sectionRef} className="py-28 bg-[#07111F] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(255,145,46,0.2)] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(139,158,192,0.15)] to-transparent" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(255,145,46,0.03) 0%, transparent 70%)' }} />
      <div className="max-w-container mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={0}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="section-label mb-4">{t('process_label')}</div>
          <h2 className="font-heading text-headline font-bold text-white mb-5">
            {t('process_headline_1')}{' '}
            <span className="text-gradient-orange">{t('process_headline_accent')}</span>
          </h2>
          <p className="text-muted text-base sm:text-lg max-w-lg mx-auto">{t('process_sub')}</p>
        </motion.div>

        {!loaded ? (
          <ProcessSkeleton />
        ) : (
          <div className="relative">
            {/* Connector line (desktop) */}
            <div className="hidden lg:block absolute top-[52px] left-[12.5%] right-[12.5%] h-px">
              <div className="h-full bg-gradient-to-r from-[rgba(255,145,46,0.1)] via-[rgba(255,145,46,0.4)] to-[rgba(255,145,46,0.1)]" />
              <div
                className="absolute top-0 left-0 h-full bg-primary transition-all duration-700 ease-out"
                style={{ width: `${(activeStep / (steps.length - 1)) * 100}%`, opacity: 0.7 }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-4">
              {steps.map((step, i) => (
                <motion.div
                  key={step.title}
                  variants={fadeUp}
                  initial="hidden"
                  animate={inView ? 'visible' : 'hidden'}
                  custom={i + 1}
                  className="cursor-pointer"
                  onClick={() => setActiveStep(i)}
                  onMouseEnter={() => setHoveredStep(i)}
                  onMouseLeave={() => setHoveredStep(null)}
                >
                  <div
                    className={`rounded-3xl p-6 sm:p-7 border process-step-hover transition-all duration-400 ${
                      activeStep === i
                        ? 'bg-[rgba(255,145,46,0.08)] border-[rgba(255,145,46,0.4)] process-step-active'
                        : hoveredStep === i
                        ? 'bg-[rgba(20,42,72,0.9)] border-[rgba(255,145,46,0.25)]'
                        : 'card-glass border-[rgba(139,158,192,0.15)]'
                    }`}
                  >
                    {activeStep === i && (
                      <div className="absolute top-0 left-4 right-4 h-px rounded-full"
                        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,145,46,0.7), transparent)' }} />
                    )}
                    <div className="flex items-center gap-4 mb-5">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-400 ${
                          activeStep === i
                            ? 'bg-primary text-white shadow-[0_0_20px_rgba(255,145,46,0.5)]'
                            : hoveredStep === i
                            ? 'bg-[rgba(255,145,46,0.18)] text-primary shadow-[0_0_12px_rgba(255,145,46,0.25)]'
                            : 'bg-[rgba(255,145,46,0.1)] text-primary'
                        }`}
                      >
                        {step.icon}
                      </div>
                      <span
                        className={`font-heading text-3xl font-bold transition-colors duration-300 ${
                          activeStep === i ? 'text-primary' : 'text-[rgba(139,158,192,0.3)]'
                        }`}
                      >
                        {step.number}
                      </span>
                    </div>
                    <h3
                      className={`font-heading text-lg sm:text-xl font-bold mb-3 transition-colors duration-300 ${
                        activeStep === i ? 'text-white' : 'text-[rgba(255,255,255,0.75)]'
                      }`}
                    >
                      {step.title}
                    </h3>
                    <p className="text-muted text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}